/*
 This file is part of GNU Taler
 (C) 2023 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { Result } from "./result.js";

/**
 * IBAN validation.
 *
 * Validates the ISO country/check-digit shape, the SWIFT registry length and
 * the MOD-97-10 checksum.  Country-specific BBAN structure checks are left to
 * the receiving bank.
 *
 * @author Florian Dold <dold@taler.net>
 * @author sebasjm
 */

declare const __iban: unique symbol;
export type IbanString = string & { [__iban]: true };

export enum ParseIbanError {
  /**
   * The country is not the one listed in https://www.swift.com/resource/iban-registry-pdf
   */
  UNSUPPORTED_COUNTRY,
  /**
   * The IBAN length should be less than 34 chars
   */
  TOO_LONG,
  /**
   * The IBAN length should be greater than 4 chars
   */
  TOO_SHORT,
  /**
   * The IBAN does not have the length assigned to its country.
   */
  INVALID_LENGTH,
  /**
   * The IBAN should only have letters and numbers
   */
  INVALID_CHARSET,
  /**
   * Computed MOD-97-10 checksum doesn't match
   */
  INVALID_CHECKSUM,
}

/**
 * @deprecated
 */
export type IbanValidationResult =
  | { type: "invalid"; code: ParseIbanError }
  | {
      type: "valid";
      normalizedIban: string;
    };

const ccZero = "0".charCodeAt(0);
const ccNine = "9".charCodeAt(0);
const ccA = "A".charCodeAt(0);
const ccZ = "Z".charCodeAt(0);

/**
 * Append a IBAN digit(s) based on a char code.
 */
function appendDigit(digits: number[], cc: number): boolean {
  if (cc >= ccZero && cc <= ccNine) {
    digits.push(cc - ccZero);
  } else if (cc >= ccA && cc <= ccZ) {
    const n = cc - ccA + 10;
    digits.push(Math.floor(n / 10) % 10);
    digits.push(n % 10);
  } else {
    return false;
  }
  return true;
}

/**
 * Compute MOD-97-10 as per ISO/IEC 7064:2003.
 */
function mod97(digits: number[]): number {
  let i = 0;
  let modAccum = 0;
  while (i < digits.length) {
    let n = 0;
    while (n < 9 && i < digits.length) {
      modAccum = modAccum * 10 + digits[i];
      i++;
      n++;
    }
    modAccum = modAccum % 97;
  }
  return modAccum;
}

export function convertHUF_BBANtoIBAN(
  value: string,
): Result<
  IbanString,
  | ParseIbanError.TOO_LONG
  | ParseIbanError.TOO_SHORT
  | ParseIbanError.INVALID_CHARSET
> {
  if (value.startsWith("HU")) {
    const maybeIban = parseIban(value);
    if (Result.isOk(maybeIban)) {
      return Result.of(maybeIban.value);
    }
  }
  if (value.length > 24) {
    return Result.error(ParseIbanError.TOO_LONG);
  }
  if (value.length < 16) {
    return Result.error(ParseIbanError.TOO_SHORT);
  }
  if (!/^[0-9]+$/.test(value)) {
    return Result.error(ParseIbanError.INVALID_CHARSET);
  }

  const bban = value.length === 16 ? `${value}00000000` : value;
  return Result.of(constructIban("HU", bban));
}

/**
 * Fake a BBAn conversion from CHF to IBAN.
 * Note that this conversion does not exist in practice,
 * it's only used for testing.
 */
export function convertCHF_BBANtoIBAN(
  value: string,
): Result<
  IbanString,
  | ParseIbanError.TOO_LONG
  | ParseIbanError.TOO_SHORT
  | ParseIbanError.INVALID_CHARSET
> {
  if (value.startsWith("CH")) {
    const maybeIban = parseIban(value);
    if (maybeIban.tag === "ok") {
      return Result.of(maybeIban.value);
    }
  }
  if (value.length > 24) {
    return Result.error(ParseIbanError.TOO_LONG);
  }
  if (value.length < 16) {
    return Result.error(ParseIbanError.TOO_SHORT);
  }
  if (!/^[0-9]+$/.test(value)) {
    return Result.error(ParseIbanError.INVALID_CHARSET);
  }

  const bban = value;
  return Result.of(constructIban("CH", bban));
}

/**
 * Check the IBAN is correct and return canonical form
 * @param ibanString
 * @returns
 */
export function parseIban(
  ibanString: string,
): Result<IbanString, ParseIbanError> {
  const myIban = ibanString.toUpperCase().replace(/[\s\-._]/g, "");
  if (myIban.length < 4) {
    return Result.error(ParseIbanError.TOO_SHORT);
  }
  // ISO 13616: two country letters, two numeric check digits, then the BBAN.
  // appendDigit expands A-Z into two digits, so alphabetic check digits would
  // otherwise still satisfy mod-97-10.
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]*$/.test(myIban)) {
    return Result.error(ParseIbanError.INVALID_CHARSET);
  }
  if (myIban.length > 34) {
    return Result.error(ParseIbanError.TOO_LONG);
  }

  const countryCode = myIban.substring(0, 2);
  const expectedLength = ibanCountryLengths[countryCode];

  if (!expectedLength) {
    return Result.error(ParseIbanError.UNSUPPORTED_COUNTRY);
  }
  if (myIban.length !== expectedLength) {
    return Result.error(ParseIbanError.INVALID_LENGTH);
  }

  let digits: number[] = [];

  for (let i = 4; i < myIban.length; i++) {
    const cc = myIban.charCodeAt(i);
    if (!appendDigit(digits, cc)) {
      return Result.error(ParseIbanError.INVALID_CHARSET);
    }
  }

  for (let i = 0; i < 4; i++) {
    const cc = myIban.charCodeAt(i);
    if (!appendDigit(digits, cc)) {
      return Result.error(ParseIbanError.INVALID_CHARSET);
    }
  }

  const rem = mod97(digits);
  if (rem === 1) {
    return Result.of(myIban as IbanString);
  } else {
    return Result.error(ParseIbanError.INVALID_CHECKSUM);
  }
}

/**
 * @deprecated use parseIban
 *
 * @param ibanString
 * @returns
 */
export function validateIban(ibanString: string): IbanValidationResult {
  const result = parseIban(ibanString);
  if (Result.isOk(result)) {
    return {
      type: "valid",
      normalizedIban: result.value,
    };
  }
  return { type: "invalid", code: result.error };
}

export function generateIban(countryCode: string, length: number): IbanString {
  let bban = "";

  for (let i = 0; i < length; i++) {
    const cc = ccZero + (Math.floor(Math.random() * 100) % 10);
    bban += String.fromCharCode(cc);
  }

  return constructIban(countryCode, bban);
}

export function constructIban(countryCode: string, bban: string): IbanString {
  let ibanSuffix = "";
  let digits: number[] = [];

  for (let i = 0; i < bban.length; i++) {
    const cc = bban.charCodeAt(i);
    appendDigit(digits, cc);
    ibanSuffix += String.fromCharCode(cc);
  }

  appendDigit(digits, countryCode.charCodeAt(0));
  appendDigit(digits, countryCode.charCodeAt(1));

  // Try using "00" as check digits
  appendDigit(digits, ccZero);
  appendDigit(digits, ccZero);

  const requiredChecksum = 98 - mod97(digits);

  const checkDigit1 = Math.floor(requiredChecksum / 10) % 10;
  const checkDigit2 = requiredChecksum % 10;

  return (countryCode + checkDigit1 + checkDigit2 + ibanSuffix) as IbanString;
}

/**
 * IBAN lengths from the SWIFT IBAN Registry.  Presence in this table is the
 * source of truth for whether a country currently participates in IBAN.
 */
export const ibanCountryLengths: Readonly<Record<string, number>> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BI: 27,
  BJ: 28,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  LY: 25,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MN: 20,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  OM: 23,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  RU: 33,
  SA: 24,
  SC: 31,
  SD: 18,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  SO: 23,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
  YE: 30,
};
